import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Badge,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Tooltip,
  useTheme,
  useMediaQuery,
  CssBaseline,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Paper,
  Popover,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle as AccountCircleIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useBackgroundJobs } from '../contexts/BackgroundJobsContext';
import { useNavigate, useLocation } from 'react-router-dom';
import Navigation from './Navigation';
import { AppAssistantFab } from './AppAssistantFab';
import { BackToTopFab, LAYOUT_MAIN_SCROLL_ID } from './BackToTopFab';
import { ServerHealthPill } from './ServerHealthPill';
import { api } from '../contexts/ApiContext';
import {
  buildDashboardReminders,
  DASHBOARD_REMINDERS_CHANGED_EVENT,
  pathForDashboardReminder,
  remindersFromRecurringTasks,
  type DashboardReminder,
  type ManualReviewSummary,
  type RecurringTaskReminderInput,
  type SupplierVatPolicyRow,
} from '../utils/dashboardReminders';
import { formatAppVersionLabel } from '../appRelease';
import { putUserUiState } from '../utils/userUiStateApi';
import { ToastCodeCaption } from './ToastCodeCaption';

interface LayoutProps {
  children: React.ReactNode;
}

function severityColor(severity: DashboardReminder['severity']): 'error' | 'warning' | 'info' | 'default' {
  if (severity === 'error') return 'error';
  if (severity === 'warning') return 'warning';
  if (severity === 'info') return 'info';
  return 'default';
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Drawer closed by default on mobile, open by default on desktop
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [notificationsAnchor, setNotificationsAnchor] = useState<null | HTMLElement>(null);
  const { user, isAuthenticated, logout } = useAuth();
  const bgJobs = useBackgroundJobs();
  const navigate = useNavigate();
  const location = useLocation();
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const [dashboardReminders, setDashboardReminders] = useState<DashboardReminder[]>([]);

  const refreshDashboardReminders = useCallback(async () => {
    try {
      const [manualRes, vatRes, taskRes] = await Promise.allSettled([
        api.get('/api/transaction-classification-rules/manual-review-count'),
        api.get('/api/suppliers/vat-invoice-policies'),
        api.get('/api/recurring-tasks'),
      ]);
      const summary: ManualReviewSummary | null =
        manualRes.status === 'fulfilled' ? (manualRes.value.data as ManualReviewSummary) || null : null;
      const rows: SupplierVatPolicyRow[] =
        vatRes.status === 'fulfilled' && Array.isArray(vatRes.value.data?.rows)
          ? (vatRes.value.data.rows as SupplierVatPolicyRow[])
          : [];
      const recurringTasks: RecurringTaskReminderInput[] =
        taskRes.status === 'fulfilled' && Array.isArray(taskRes.value.data?.tasks)
          ? (taskRes.value.data.tasks as RecurringTaskReminderInput[])
          : [];
      setDashboardReminders([
        ...buildDashboardReminders(summary, rows, user?.role),
        ...remindersFromRecurringTasks(recurringTasks),
      ]);
    } catch {
      setDashboardReminders([]);
    }
  }, [user?.role]);

  useEffect(() => {
    if (!user) return;
    void refreshDashboardReminders();
  }, [user, refreshDashboardReminders]);

  useEffect(() => {
    if (!user) return;
    if (location.pathname !== '/dashboard') return;
    void refreshDashboardReminders();
  }, [user, location.pathname, refreshDashboardReminders]);

  useEffect(() => {
    if (!user) return;
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        void refreshDashboardReminders();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [user, refreshDashboardReminders]);

  useEffect(() => {
    if (!user) return;
    const handler = () => {
      void refreshDashboardReminders();
    };
    window.addEventListener(DASHBOARD_REMINDERS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(DASHBOARD_REMINDERS_CHANGED_EVENT, handler);
  }, [user, refreshDashboardReminders]);

  // Set initial drawer state based on screen size
  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  const handleDrawerToggle = () => {
    // Only allow toggling on mobile
    if (isMobile) {
      setDrawerOpen(!drawerOpen);
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchor(event.currentTarget);
    void refreshDashboardReminders();
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  const handleLogout = () => {
    logout();
    setProfileMenuAnchor(null);
  };

  const dismissReminder = async (reminder: DashboardReminder) => {
    if (!reminder.dismissStorageKey) return;
    try {
      localStorage.setItem(reminder.dismissStorageKey, '1');
    } catch {
      // ignore
    }
    if (reminder.key === 'post-deploy-sql-migrations-apr2026') {
      void putUserUiState('dashboard.reminder.post_deploy', { dismissed: true });
    }
    window.dispatchEvent(new CustomEvent(DASHBOARD_REMINDERS_CHANGED_EVENT));
    void refreshDashboardReminders();
  };

  const badgeCount =
    dashboardReminders.length +
    (bgJobs.isRunning ? 1 : 0) +
    (bgJobs.latestNotice ? 1 : 0);
  const notificationsOpen = Boolean(notificationsAnchor);

  // Don't show layout for auth pages
  if (!isAuthenticated) {
    return (
      <>
        <CssBaseline />
        {children}
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', overflow: 'hidden' }}>
      <CssBaseline />
      
      {/* Navigation Drawer */}
      <Navigation open={drawerOpen} onToggle={handleDrawerToggle} />
      
      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          width: '100%',
          position: 'relative',
          overflow: 'hidden', // Prevent main container from scrolling
        }}
      >
        {/* Top App Bar */}
        <AppBar
          position="fixed"
          sx={{
            zIndex: theme.zIndex.drawer + 1,
            backgroundColor: 'background.paper',
            color: 'text.primary',
            boxShadow: 1,
            borderBottom: 1,
            borderColor: 'divider'
          }}
        >
          <Toolbar>
            {/* Only show hamburger menu on mobile */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="toggle drawer"
                onClick={handleDrawerToggle}
                edge="start"
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Property Portfolio Manager
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mr: 1.5, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
            >
              {formatAppVersionLabel()}
            </Typography>
            
            {user && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title="Notifications">
                  <IconButton
                    color="inherit"
                    size="small"
                    aria-label={`Notifications, ${badgeCount} active`}
                    aria-controls={notificationsOpen ? 'app-notifications-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={notificationsOpen ? 'true' : undefined}
                    onClick={handleNotificationsOpen}
                    sx={{ mr: 0.5 }}
                  >
                    <Badge
                      color="warning"
                      badgeContent={badgeCount}
                      invisible={badgeCount === 0}
                      max={99}
                    >
                      <NotificationsIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
                <Popover
                  id="app-notifications-menu"
                  open={notificationsOpen}
                  anchorEl={notificationsAnchor}
                  onClose={handleNotificationsClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1,
                        width: { xs: 'min(100vw - 24px, 420px)', sm: 420 },
                        maxHeight: 'min(70vh, 560px)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                      },
                    },
                  }}
                >
                  <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Notifications
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Reminders and background job updates
                    </Typography>
                  </Box>
                  <Box sx={{ overflow: 'auto', flex: 1, p: 1 }}>
                    {bgJobs.isRunning ? (
                      <Paper variant="outlined" sx={{ p: 1.25, mb: 1, bgcolor: 'action.hover' }}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <CircularProgress size={16} sx={{ mt: 0.35 }} />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {bgJobs.activeJobKind === 'tag_wipe' ? 'Tag wipe running' : 'Rule sync running'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                              {bgJobs.progressMessage || 'Working on the server…'}
                            </Typography>
                            <ToastCodeCaption
                              jobId={bgJobs.activeJobId || undefined}
                              copyBody={
                                [
                                  bgJobs.activeJobKind === 'tag_wipe' ? 'Tag wipe running' : 'Rule sync running',
                                  bgJobs.progressMessage || 'Working on the server…',
                                  bgJobs.detailedProgressMessage &&
                                  bgJobs.detailedProgressMessage !== bgJobs.progressMessage
                                    ? `Detail: ${bgJobs.detailedProgressMessage}`
                                    : ''
                                ]
                                  .filter(Boolean)
                                  .join('\n')
                              }
                            />
                            <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                              <Button
                                size="small"
                                onClick={() => {
                                  bgJobs.clearCompletionNotice();
                                  handleNotificationsClose();
                                  navigate('/transaction-classification-review');
                                }}
                              >
                                View Progress
                              </Button>
                              <Button
                                size="small"
                                color="warning"
                                disabled={bgJobs.cancelRequested}
                                onClick={() => void bgJobs.requestCancel()}
                              >
                                {bgJobs.cancelRequested ? 'Cancelling…' : 'Cancel Process'}
                              </Button>
                            </Stack>
                          </Box>
                        </Stack>
                      </Paper>
                    ) : null}

                    {bgJobs.latestNotice ? (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          mb: 1,
                          borderColor:
                            bgJobs.latestNotice.severity === 'error'
                              ? 'error.light'
                              : bgJobs.latestNotice.severity === 'warning'
                                ? 'warning.light'
                                : 'success.light',
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Chip
                              size="small"
                              label={bgJobs.latestNotice.severity === 'error' ? 'Failed' : bgJobs.latestNotice.severity === 'warning' ? 'Cancelled' : 'Completed'}
                              color={
                                bgJobs.latestNotice.severity === 'error'
                                  ? 'error'
                                  : bgJobs.latestNotice.severity === 'warning'
                                    ? 'warning'
                                    : 'success'
                              }
                              sx={{ mb: 0.5 }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {bgJobs.latestNotice.title}
                            </Typography>
                            {bgJobs.latestNotice.segments
                              .slice(0, bgJobs.latestNotice.jobKind === 'tag_wipe' ? 8 : 3)
                              .map((seg) => (
                              <Typography key={seg} variant="caption" color="text.secondary" display="block">
                                {seg}
                              </Typography>
                            ))}
                            <ToastCodeCaption
                              requestId={bgJobs.latestNotice.requestId}
                              jobId={bgJobs.latestNotice.jobId}
                              copyBody={[bgJobs.latestNotice.title, ...bgJobs.latestNotice.segments]
                                .filter(Boolean)
                                .join('\n')}
                            />
                          </Box>
                          <Button size="small" onClick={() => bgJobs.clearCompletionNotice()}>
                            Dismiss
                          </Button>
                        </Stack>
                      </Paper>
                    ) : null}

                    {dashboardReminders.length === 0 && !bgJobs.isRunning && !bgJobs.latestNotice ? (
                      <Box sx={{ px: 1.5, py: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          No notifications right now.
                        </Typography>
                      </Box>
                    ) : null}

                    {dashboardReminders.map((reminder) => (
                      <Paper
                        key={reminder.key}
                        variant="outlined"
                        sx={{ p: 1.25, mb: 1, cursor: 'pointer' }}
                        onClick={() => {
                          handleNotificationsClose();
                          navigate(pathForDashboardReminder(reminder));
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <Chip size="small" label={reminder.source} color={severityColor(reminder.severity)} variant="outlined" />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {reminder.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              {reminder.detail}
                            </Typography>
                            {reminder.dueDate ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                Due {reminder.dueDate.toLocaleDateString()}
                              </Typography>
                            ) : null}
                            <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                              <Button
                                size="small"
                                endIcon={<OpenInNewIcon fontSize="inherit" />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleNotificationsClose();
                                  navigate(pathForDashboardReminder(reminder));
                                }}
                              >
                                Open
                              </Button>
                              {reminder.dismissStorageKey ? (
                                <Button
                                  size="small"
                                  color="inherit"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void dismissReminder(reminder);
                                  }}
                                >
                                  Dismiss
                                </Button>
                              ) : null}
                            </Stack>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                  <Box sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Button
                      fullWidth
                      size="small"
                      onClick={() => {
                        handleNotificationsClose();
                        navigate('/dashboard');
                      }}
                    >
                      View all on Dashboard
                    </Button>
                  </Box>
                </Popover>
                <ServerHealthPill />
                <IconButton
                  onClick={handleProfileMenuOpen}
                  size="small"
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: 'inherit',
                    textTransform: 'none'
                  }}
                >
                  <Typography variant="body2">
                    {user.first_name} {user.last_name}
                  </Typography>
                  <AccountCircleIcon />
                </IconButton>
                
                {/* Profile Dropdown Menu */}
                <Menu
                  anchorEl={profileMenuAnchor}
                  open={Boolean(profileMenuAnchor)}
                  onClose={handleProfileMenuClose}
                  onClick={handleProfileMenuClose}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                      mt: 1.5,
                      '& .MuiAvatar-root': {
                        width: 32,
                        height: 32,
                        ml: -0.5,
                        mr: 1,
                      },
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem onClick={() => {
                    navigate('/profile');
                    handleProfileMenuClose();
                  }}>
                    <ListItemIcon>
                      <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>User Profile</ListItemText>
                  </MenuItem>
                  {user.role === 'owner' && (
                    <MenuItem
                      onClick={() => {
                        navigate('/settings');
                        handleProfileMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <SettingsIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Settings</ListItemText>
                    </MenuItem>
                  )}
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Logout</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Box
          ref={mainScrollRef}
          id={LAYOUT_MAIN_SCROLL_ID}
          sx={{
            flexGrow: 1,
            p: 3,
            mt: '64px', // Account for AppBar height
            backgroundColor: 'background.default',
            minHeight: 'calc(100vh - 64px)',
            height: 'calc(100vh - 64px)',
            overflow: 'auto',
            overflowY: 'scroll',
            WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
            position: 'relative',
            zIndex: 10, // Ensure content is well above drawer
          }}
        >
          {children}
        </Box>
        <BackToTopFab key={location.pathname} scrollRef={mainScrollRef} />
        <AppAssistantFab />
      </Box>
    </Box>
  );
};

export default Layout;
